import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    if (window.location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'backdrop-blur-xl bg-white/90 shadow-md' : 'bg-white'
      } border-b border-slate-200`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_530bf07d-b9ef-426c-bee3-64db76b4583b/artifacts/8lsf1zng_logo%20no.%203.jpeg"
              alt="Ashvya Digital Media"
              className="h-16 w-auto object-contain"
              style={{ objectPosition: 'center' }}
              data-testid="header-logo"
            />
          </Link>

          {/* Desktop Menu */}
          <nav className="hidden md:flex items-center space-x-8" data-testid="desktop-nav">
            <button
              onClick={() => scrollToSection('home')}
              className="text-slate-700 hover:text-teal-600 font-medium transition-colors"
              data-testid="nav-home"
            >
              Home
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="text-slate-700 hover:text-teal-600 font-medium transition-colors"
              data-testid="nav-about"
            >
              About Us
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className="text-slate-700 hover:text-teal-600 font-medium transition-colors"
              data-testid="nav-services"
            >
              Services
            </button>
            <button
              onClick={() => scrollToSection('testimonials')}
              className="text-slate-700 hover:text-teal-600 font-medium transition-colors"
              data-testid="nav-testimonials"
            >
              Testimonials
            </button>
            <Link
              to="/blog"
              className="text-slate-700 hover:text-teal-600 font-medium transition-colors"
              data-testid="nav-blog"
            >
              Blog
            </Link>
            <button
              onClick={() => scrollToSection('contact')}
              className="bg-teal-500 hover:bg-teal-600 text-white px-6 py-2.5 rounded-full font-semibold transition-all duration-200 shadow-sm hover:shadow-md"
              data-testid="nav-contact-btn"
            >
              Contact Us
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-slate-700 hover:text-teal-600"
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          className="md:hidden bg-white border-t border-slate-200 shadow-lg"
          data-testid="mobile-menu"
        >
          <nav className="flex flex-col space-y-4 px-4 py-6">
            <button
              onClick={() => scrollToSection('home')}
              className="text-slate-700 hover:text-teal-600 font-medium text-left"
              data-testid="mobile-nav-home"
            >
              Home
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="text-slate-700 hover:text-teal-600 font-medium text-left"
              data-testid="mobile-nav-about"
            >
              About Us
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className="text-slate-700 hover:text-teal-600 font-medium text-left"
              data-testid="mobile-nav-services"
            >
              Services
            </button>
            <button
              onClick={() => scrollToSection('testimonials')}
              className="text-slate-700 hover:text-teal-600 font-medium text-left"
              data-testid="mobile-nav-testimonials"
            >
              Testimonials
            </button>
            <Link
              to="/blog"
              onClick={() => setIsMobileMenuOpen(false)}
              className="text-slate-700 hover:text-teal-600 font-medium"
              data-testid="mobile-nav-blog"
            >
              Blog
            </Link>
            <button
              onClick={() => scrollToSection('contact')}
              className="bg-teal-500 hover:bg-teal-600 text-white px-6 py-2.5 rounded-full font-semibold transition-colors w-full"
              data-testid="mobile-nav-contact-btn"
            >
              Contact Us
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;